import { 
    Box, 
    Button, 
    Checkbox, 
    CircularProgress, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    FormControlLabel, 
    Stack, 
    Switch, 
    TextField,
    Typography,
    Divider,
    Paper,
    Chip,
    IconButton,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel
} from "@mui/material";
import { Timer, SmartToy, Close, Collections, Storage, Lock, Public, MicRounded, DesktopWindows } from "@mui/icons-material";
import { useState } from "react";
import StarIcon from '../assets/icons/star.png';
import { Collection, createNewCollection } from "../../../../models/collection";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import { Lecture, newLecture, LectureAccess } from "../../../../models/lecture";

interface NewLectureDialogProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    classroom_id: string;
    setRunningTask: React.Dispatch<React.SetStateAction<number>>;
}

export default function NewLectureDialog({ open, setOpen, classroom_id , setRunningTask}: NewLectureDialogProps) {

    const { createLecture } = useClassroomActions();

    const [lecture, setLecture] = useState<Lecture>(newLecture());
    const handleLectureChange = (field: keyof Lecture, value: any) => {
        setLecture((prev) => ({
            ...prev,
            [field]: value
        }));
    }

    const handleAccessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value) as LectureAccess;
        handleLectureChange("access", value);
    }

    const handleSubmit = async () => {
        setRunningTask(lecture.id);
        setOpen(false);
        await createLecture(Number(classroom_id), lecture);
        setRunningTask(-1);
        setLecture(newLecture());
    }

    return (
        <Dialog 
            open={open} 
            onClose={() => setOpen(false)} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                }
            }}
        >
            <DialogTitle 
                sx={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '20px 24px',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #10b981, #059669, #10b981)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite',
                    },
                    '@keyframes shimmer': {
                        '0%': { backgroundPosition: '-200% 0' },
                        '100%': { backgroundPosition: '200% 0' },
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <DesktopWindows/>
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                            Schedule New Lecture
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                            Pre-record or take your students live
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            transform: 'scale(1.1)',
                        },
                        '&:disabled': {
                            color: 'rgba(255,255,255,0.5)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                <Stack spacing={3} pt={2}>
                    <TextField
                        label="Lecture Title"
                        value={lecture.title}
                        onChange={(e) => handleLectureChange("title", e.target.value)}
                        fullWidth
                        sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f8f9fa',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#f0f1f3',
                            },
                            '&.Mui-focused': {
                                backgroundColor: '#ffffff',
                                boxShadow: '0 0 0 3px rgba(83, 130, 107, 0.1)',
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#53826b',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#53826b',
                            borderWidth: 2,
                        },
                        }}
                    />

                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2.5, 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: 2,
                            border: '1px solid #e5e7eb',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#f0f1f3',
                                borderColor: '#d1d5db',
                            }
                        }}
                    >
                        <FormControl component="fieldset">
                            <FormLabel 
                                component="legend" 
                                sx={{ 
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    mb: 1.5,
                                    '&.Mui-focused': {
                                        color: '#53826b',
                                    }
                                }}
                            >
                                Lecture Access
                            </FormLabel>
                            <RadioGroup
                                value={lecture.access.toString()}
                                onChange={handleAccessChange}
                                sx={{ gap: 1 }}
                            >
                                <FormControlLabel
                                    value={LectureAccess.PRIVATE.toString()}
                                    control={
                                        <Radio 
                                            sx={{
                                                color: '#9ca3af',
                                                '&.Mui-checked': {
                                                    color: '#10b981',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Lock sx={{ fontSize: 18, color: '#6b7280' }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={500} color="text.primary">
                                                    Private
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Only you can access
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{ 
                                        mx: 0,
                                        p: 1,
                                        borderRadius: 1.5,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(16, 185, 129, 0.05)',
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    value={LectureAccess.PUBLIC.toString()}
                                    control={
                                        <Radio 
                                            disabled
                                            sx={{
                                                color: '#9ca3af',
                                                '&.Mui-checked': {
                                                    color: '#10b981',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Public sx={{ fontSize: 18, color: '#6b7280' }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={500} color="text.primary">
                                                    Public
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Your students can access
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{ 
                                        mx: 0,
                                        p: 1,
                                        borderRadius: 1.5,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(16, 185, 129, 0.05)',
                                        }
                                    }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Paper>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!lecture.title}
                    sx={{ 
                        backgroundColor: "#10b981",
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 4,
                        py: 1,
                        minWidth: 160,
                        '&:hover': {
                            backgroundColor: "#059669"
                        },
                        '&:disabled': {
                            backgroundColor: '#e5e7eb'
                        }
                    }}
                >
                    Schedule Lecture
                </Button>
            </DialogActions>
        </Dialog>
    );
}